<template>
  <BaseSimpleLayout class="device-edit">
    <template #title>端末情報編集</template>
    <template #main>
      <el-form
        ref="formRef"
        :model="editForm"
        :rules="rules"
        size="large"
        label-width="150px"
      >
        <div class="input-area">
          <el-form-item label="ID" prop="id">
            <el-input v-model="editForm.id" autocomplete="off" disabled />
          </el-form-item>
          <el-form-item label="ステータス" prop="status">
            <el-select
              v-model="editForm.status"
              placeholder=""
              :disabled="!canEditStatus"
            >
              <el-option
                v-for="item in deviceStatusList"
                :key="item.id"
                :label="item.name"
                :value="item.id"
              >
              </el-option>
            </el-select>
          </el-form-item>
          <el-form-item
            v-if="!isMccsTypeT(editForm.deviceType)"
            label="SIM番号"
            prop="simNumber"
          >
            <el-input
              v-model="editForm.simNumber"
              autocomplete="off"
              disabled
            />
          </el-form-item>
          <el-form-item label="IMSI" prop="simImsi">
            <el-input v-model="editForm.simImsi" autocomplete="off" disabled />
          </el-form-item>
          <el-form-item label="シリアル番号" prop="serialCode">
            <el-input
              v-model="editForm.serialCode"
              autocomplete="off"
              :disabled="!editable"
            />
          </el-form-item>
          <el-form-item label="端末タイプ" prop="deviceType">
            <el-select
              v-model="editForm.deviceType"
              :disabled="isReady"
              placeholder=""
              @change="changeSerialInitial"
            >
              <el-option
                v-for="item in deviceTypeList"
                :key="item.id"
                :label="item.name"
                :value="item.id"
              />
            </el-select>
          </el-form-item>
          <el-form-item label="マイナーVer." prop="deviceMinorVersion">
            <el-select
              v-model="editForm.deviceMinorVersion"
              :disabled="isDeviceMinorVersionDisabled"
              placeholder=""
              @change="changeSerialInitial"
            >
              <el-option
                v-for="item in deviceMinorVersionList"
                :key="item.name"
                :label="item.name"
                :value="item.name"
              />
            </el-select>
          </el-form-item>
          <el-form-item label="製造番号" prop="deviceCode">
            <el-input
              v-model="editForm.deviceCode"
              autocomplete="off"
              @change="changeString(editForm, 'deviceCode')"
            />
          </el-form-item>
          <el-form-item label="取扱" prop="handlingType">
            <el-select v-model="editForm.handlingType" placeholder="">
              <el-option
                v-for="item in handlingTypeList"
                :key="item.id"
                :label="item.name"
                :value="item.id"
              >
              </el-option>
            </el-select>
          </el-form-item>

          <el-form-item
            label="確定端末"
            prop="orderCompanyId"
            class="device-edit-order-company"
          >
            <el-select
              v-model="editForm.orderCompanyId"
              :disabled="!editable"
              filterable
              clearable
              placeholder=""
              @change="syncItemIdFromMinor"
            >
              <el-option
                v-for="item in companiesList"
                :key="item.id"
                :label="item.name"
                :value="item.id"
              />
            </el-select>
          </el-form-item>
        </div>
        <div class="input-area">
          <el-form-item
            :disabled="!editable"
            label="出荷ファームVer"
            prop="firmVersion"
          >
            <el-input v-model="editForm.firmVersion" autocomplete="off" />
          </el-form-item>
          <el-form-item label="出荷コンフィグ" prop="configName">
            <el-input
              v-model="editForm.configName"
              autocomplete="off"
              disabled
            />
          </el-form-item>
          <el-form-item
            :disabled="!editable"
            label="IBバッテリー"
            prop="internalBatteryVoltage"
          >
            <el-input
              v-model="editForm.internalBatteryVoltage"
              autocomplete="off"
            />
          </el-form-item>
          <el-form-item label="Mboot" prop="mboot">
            <el-input
              v-model="editForm.mboot"
              autocomplete="off"
              :disabled="!editable"
            />
          </el-form-item>
          <el-form-item label="設定ID" prop="deviceSettingUser">
            <el-input
              v-model="editForm.deviceSettingUser"
              autocomplete="off"
              disabled
            />
          </el-form-item>
          <el-form-item label="設定パスワード" prop="deviceSettingPassword">
            <el-input
              v-model="editForm.deviceSettingPassword"
              autocomplete="off"
              disabled
            />
          </el-form-item>
          <el-form-item label="備考" prop="note">
            <el-input
              v-model="editForm.note"
              type="textarea"
              :rows="3"
              autocomplete="off"
            />
          </el-form-item>
        </div>
      </el-form>
      <div class="button-area">
        <el-button
          type="info"
          size="large"
          @click="router.push(`/device/${editForm.id}/detail`)"
        >
          戻る
        </el-button>
        <el-button type="primary" size="large" @click="moveConfirm(formRef)">
          確認
        </el-button>
      </div>
    </template>
  </BaseSimpleLayout>
</template>

<script setup lang="ts">
  import { ref } from 'vue';
  import { useStore } from 'vuex';
  import router from '@/router';
  import BaseSimpleLayout from '@/components/BaseSimpleLayout.vue';
  import type { FormInstance, FormRules } from 'element-plus';
  import { DevicePutData } from '@/types/device';
  import { CompaniesDataState } from '@/types/master/company';
  import {
    // validateSerial,
    deviceTypeList,
    deviceMinorVersionList,
    // verifyEditRule,
    getEditRule,
    handlingTypeList,
    deviceStatusList,
    resolveMastMinorItemId,
    isTokaiRikaOrderCompany,
    fetchTokaiRikaCompanyIds,
    isMccsTypeT
  } from '@/libs/device';
  import { alphanumericRestriction } from '@/libs/restriction';
  import { hasScreenCodePermission } from '@/libs/auth';

  const store = useStore();
  const isDeviceMinorVersionDisabled = ref(false);
  const formRef = ref<FormInstance>();
  const isReady = ref(false);
  const editable = ref(false);
  const tokaiRikaCompanyIds = ref<Set<number>>(new Set());
  const editForm = ref<DevicePutData>({
    id: 0,
    deviceCode: '',
    deviceType: 3,
    serialCode: '',
    simNumber: '',
    simImsi: '',
    firmVersion: '',
    configName: '',
    deviceSettingUser: '',
    deviceSettingPassword: '',
    internalBatteryVoltage: '',
    mboot: '',
    orderCompanyId: 0,
    note: '',
    handlingType: 0,
    deviceMinorVersion: 'J02',
    itemId: 494,
    status: 0
  });
  // const rules = ref<FormRules>({
  //   serialCode: [
  //     { required: true, message: 'シリアル番号は必須です。' },
  //     { validator: validateSerial, trigger: 'blur' }
  //   ]
  // });
  const rules = ref<FormRules>({});
  const companiesList = ref<CompaniesDataState[]>([]);
  const canEditStatus = ref(false);

  const syncItemIdFromMinor = () => {
    if (editForm.value.deviceType !== 3) {
      editForm.value.itemId = null;
      return;
    }
    const isTokai = isTokaiRikaOrderCompany(
      editForm.value.orderCompanyId,
      tokaiRikaCompanyIds.value
    );
    editForm.value.itemId = resolveMastMinorItemId(
      editForm.value.deviceMinorVersion,
      isTokai
    );
  };

  const initialize = async () => {
    if (!store.state.device.putData) {
      router.push('/device');
      return;
    }
    editForm.value = store.state.device.putData;
    tokaiRikaCompanyIds.value = await fetchTokaiRikaCompanyIds();
    const permissionPrefix =
      editForm.value.deviceType === 2
        ? 'DEVICE_II'
        : editForm.value.deviceType === 11
          ? 'DEVICE_T'
          : 'DEVICE_III';
    canEditStatus.value = hasScreenCodePermission(
      `${permissionPrefix}-EDIT_STATUS`
    );

    const status = store.state.device.status;
    isReady.value = status > 1;
    editable.value = status === 1 || status === 2;
    companiesList.value = store.state.companiesMaster.listData.filter(
      (data: CompaniesDataState) => data.isAdvanceOrder
    );
    if (status >= 2) {
      // rules.value = verifyEditRule;
      rules.value = getEditRule(editForm.value.deviceType);
    }
    isDeviceMinorVersionDisabled.value = editForm.value.deviceType !== 3;
  };

  const moveConfirm = async (formEl: FormInstance | undefined) => {
    if (!formEl) return;
    await formEl.validate(async (valid) => {
      if (!valid) {
        return false;
      }
      if (!editForm.value.orderCompanyId) editForm.value.orderCompanyId = null;
      syncItemIdFromMinor();

      store.dispatch('device/setPutData', editForm.value);
      router.push('/device/edit/confirm');
    });
  };
  const changeSerialInitial = () => {
    const { deviceType, deviceMinorVersion } = editForm.value;

    if (deviceType === 3) {
      isDeviceMinorVersionDisabled.value = false;
      if (!deviceMinorVersion) {
        editForm.value.deviceMinorVersion = 'J02';
      }
      syncItemIdFromMinor();
    } else {
      isDeviceMinorVersionDisabled.value = true;
      editForm.value.deviceMinorVersion = '';
      editForm.value.itemId = null;
    }
  };

  const changeString = (form: DevicePutData, key: string) => {
    form[key] = alphanumericRestriction(form[key] as string);
  };
  initialize();
</script>
<style lang="scss">
  .device-edit {
    form {
      width: 1000px;
      margin: auto;
      height: 480px;
      .el-form-item {
        font-weight: 500 !important;
        .el-form-item__label {
          font-weight: 600;
        }
      }
      .input-area {
        float: left;
        .el-input {
          width: 200px;
          float: left;
        }
        .el-select {
          float: left;
        }
        .device-edit-order-company {
          .el-input {
            width: 300px;
            float: left;
          }
        }
        .el-input.is-disabled .el-input__inner {
          color: #606266;
        }
        textarea {
          width: 300px;
        }
      }
    }
    .button-area {
      margin: 20px 0px;
      .el-button {
        font-weight: 600;
        margin: 20px 10px;
      }
    }
  }
</style>
